// common/shared configs
import { amazonArticle } from "@/config/reach/common/amazon/amazonArticle";
import { amazonCarousel } from "@/config/reach/common/amazon/amazonCarousel";
import { amazonConfig } from "@/config/reach/common/amazon/amazonConfig";
import auctionConfig from "@/config/reach/common/auction.json";
import consentConfig from "@/config/reach/common/consent.json";
import { carouselAdSizesGPT } from "@/config/reach/common/gpt/carouselAdSizes";
import gptConfig from "@/config/reach/common/gpt/gptConfig.json";
import gptRefreshConfig from "@/config/reach/common/gptRefresh.json";
import loginRadiusConfig from "@/config/reach/common/loginradius.json";
import articleAdSizesMagnite from "@/config/reach/common/magnite/articleAdSizes.json";
import magniteBidderSettings from "@/config/reach/common/magnite/bidderSettings.json";
import carouselAdSizesMagnite from "@/config/reach/common/magnite/carouselAdSizes.json";
import magniteConfig from "@/config/reach/common/magnite/magniteConfig.json";
import pbjsConfig from "@/config/reach/common/pbjsConfig.json";
import bidderSettings from "@/config/reach/common/prebid/bidderSettings.json";
import consentManagement from "@/config/reach/common/prebid/consentManagement.json";
import currency from "@/config/reach/common/prebid/currency.json";
import mediaTypePriceGranularity from "@/config/reach/common/prebid/mediaTypePriceGranularity.json";
import prebidModuleFlags from "@/config/reach/common/prebid/moduleConfig.json";
import ozone from "@/config/reach/common/prebid/ozone.json";
import paapiForGpt from "@/config/reach/common/prebid/paapiForGpt.json";
import userSync from "@/config/reach/common/prebid/userSync.json";
import { sequenceModule } from "@/config/reach/common/sequenceModule.ts";
// site-specific configs
import carouselAdUnits from "@/config/reach/leicestermercury/article/prebid/carouselAdUnits.json";
import displayAdUnits from "@/config/reach/leicestermercury/article/prebid/displayAdUnits.json";
import outstreamAdUnits from "@/config/reach/leicestermercury/article/prebid/outstreamAdUnits.json";
import videoAdUnits from "@/config/reach/leicestermercury/article/prebid/videoAdUnits.json";
import bartConfig from "@/config/reach/leicestermercury/common/bartConfig.json";
// modules
import { AmazonTAM } from "@/module/AmazonTAM";
import { AuctionController } from "@/module/AuctionController";
import { AuctionPageData } from "@/module/AuctionPageData";
import { BART } from "@/module/BART";
import { Blockthrough } from "@/module/Blockthrough";
import { Consent } from "@/module/Consent";
import { GPT } from "@/module/GPT";
import { GPTRefresh } from "@/module/GPTRefresh";
import { LiveRamp } from "@/module/LiveRamp";
import { LoginRadius } from "@/module/LoginRadius";
import { MagniteDemandManager } from "@/module/MagniteDemandManager";
import { Ozone } from "@/module/Ozone";
import { Prebid } from "@/module/Prebid";
import { AdUnitPath } from "@/module/reach/AdUnitPath";
import { SequenceController } from "@/module/SequenceController";
import { SlotDiscovery } from "@/module/SlotDiscovery";
import { TakeoverDetection } from "@/module/TakeoverDetection";

import type {
	AmazonModuleConfig,
	GPTModuleConfig,
	MagniteModuleConfig,
	PrebidModuleConfig,
	SequenceModuleConfig,
} from "@/ts/module";
import type { PrebidConfig } from "@/ts/prebid/types";
import { initModulesAndCommandQueue } from "@/utils/initialize";

const sequenceModuleConfig: SequenceModuleConfig = sequenceModule;

const amazonModuleConfig: AmazonModuleConfig = {
	...amazonConfig,
	adSizesAmazon: {
		...amazonArticle.adSizesAmazon,
		...amazonCarousel.adSizesAmazon,
	},
};

const adSizesMagnite = {
	...articleAdSizesMagnite.adSizesMagnite,
	...carouselAdSizesMagnite.adSizesMagnite,
};
const magniteModuleConfig: MagniteModuleConfig = {
	...magniteConfig,
	adSizesMagnite,
	pbjsConfig,
	bidderSettings: magniteBidderSettings,
};

const prebidConfig: PrebidConfig = {
	...consentManagement,
	...currency,
	...mediaTypePriceGranularity,
	...ozone,
	...userSync,
	...pbjsConfig,
	...paapiForGpt,
};

const prebidModuleConfig: PrebidModuleConfig = {
	...prebidModuleFlags,
	prebidConfig,
	carouselAdUnits,
	displayAdUnits,
	videoAdUnits,
	outstreamAdUnits,
	bidderSettings,
};

const gptModuleConfig: GPTModuleConfig = {
	...carouselAdSizesGPT,
	...gptConfig,
};

export const initApp = async () => {
	const modules = [
		// first, modules that others might subscribe to
		new Consent(consentConfig),
		new SlotDiscovery(),
		new GPT(gptModuleConfig),
		new AmazonTAM(amazonModuleConfig),
		new MagniteDemandManager(magniteModuleConfig),
		new Prebid(prebidModuleConfig),
		new AuctionController(auctionConfig),
		new GPTRefresh(gptRefreshConfig),
		// modules that don't use subscriptions
		new BART(bartConfig),
		new LiveRamp(),
		new LoginRadius(loginRadiusConfig),
		new AdUnitPath(),
		new Ozone(),
		new Blockthrough(),
		new TakeoverDetection(),
		// last, modules that subscribe to others
		new AuctionPageData(),
		new SequenceController(sequenceModuleConfig),
	];
	await initModulesAndCommandQueue(modules);
};

initApp();
